import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import FormInput from '../ui/FormInput';
import FileUpload from '../ui/FileUpload'; // Import FileUpload
import { UserCircleIcon, AcademicCapIcon, SparklesIcon } from '@heroicons/react/24/solid'; // Import SVG icons

interface AffiliateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (affiliate: any, profileImage: File | null, selectedSvg: string | null) => void; // Replace 'any' with actual Affiliate type
  initialData?: any | null; // Replace 'any' with actual Affiliate type
}

const getSvgComponent = (svgName: string, className: string) => {
  switch (svgName) {
    case 'UserCircleIcon': return <UserCircleIcon className={className} />;
    case 'AcademicCapIcon': return <AcademicCapIcon className={className} />;
    case 'SparklesIcon': return <SparklesIcon className={className} />;
    default: return null;
  }
};

const AffiliateFormModal: React.FC<AffiliateFormModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState({
    id: '', // Add id to the formData state
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [profileImage, setProfileImage] = useState<File | null>(null); // State for uploaded image
  const [selectedSvg, setSelectedSvg] = useState<string | null>(null); // State for selected SVG

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id || '', // Include ID for editing
        name: initialData.name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        password: '', // Passwords are not pre-filled for security
        confirmPassword: '',
      });
      setProfileImage(initialData.imageFile || null); // Assuming imageFile might be passed for editing
      setSelectedSvg(initialData.imageSvg || null); // Assuming imageSvg might be passed for editing
    } else {
      setFormData({
        id: '', // Initialize ID for new affiliate
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
      });
      setProfileImage(null);
      setSelectedSvg(null);
    }
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (file: File | null) => {
    setProfileImage(file);
    setSelectedSvg(null); // Clear SVG selection if image is uploaded
  };

  const handleSvgSelect = (svgName: string) => {
    setSelectedSvg(svgName);
    setProfileImage(null); // Clear image selection if SVG is selected
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password || formData.confirmPassword) { // Only check if either field is filled
      if (formData.password !== formData.confirmPassword) {
        console.error('Passwords do not match!');
        // You might want to add a toast notification here
        return;
      }
    }
    console.log('Submitting affiliate data:', formData, 'Image:', profileImage, 'SVG:', selectedSvg);
    onSubmit({ ...formData, id: initialData?.id }, profileImage, selectedSvg); // Ensure ID is passed for updates
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold text-primary mb-6">
          {initialData ? 'Edit Affiliate' : 'Add New Affiliate'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            label="Full Name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <FormInput
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <FormInput
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />

          {/* Photo Upload or SVG Selection */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-neutral-700">Profile Photo</label>
            <FileUpload onFileSelect={handleImageUpload} />
            <p className="text-center text-neutral-500">OR select an avatar:</p>
            <div className="flex justify-center space-x-4">
              <div
                className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'UserCircleIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                onClick={() => handleSvgSelect('UserCircleIcon')}
              >
                <UserCircleIcon className="w-12 h-12 text-neutral-600" />
              </div>
              <div
                className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'AcademicCapIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                onClick={() => handleSvgSelect('AcademicCapIcon')}
              >
                <AcademicCapIcon className="w-12 h-12 text-neutral-600" />
              </div>
              <div
                className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'SparklesIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                onClick={() => handleSvgSelect('SparklesIcon')}
              >
                <SparklesIcon className="w-12 h-12 text-neutral-600" />
              </div>
            </div>
            {selectedSvg && (
              <div className="text-center mt-2">
                <p className="text-sm text-neutral-600">Selected Avatar:</p>
                {getSvgComponent(selectedSvg, "w-16 h-16 text-neutral-400 mx-auto")}
              </div>
            )}
          </div>

          <FormInput
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
          />
          <FormInput
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {initialData ? 'Save Changes' : 'Add Affiliate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateFormModal;